// useEmployeeDisplayInfo — Reusable hook for centralized employee name resolution.
// Calls GET /api/operator/employee/:employeeNumber (single) or /employees/batch (batch).
// Provides in-memory caching, single-item and batch lookups.
// Name priority (resolved on backend): middle_name → first_name → full_name → 'Employee ' + number

import { useState, useCallback, useRef } from 'react';
import operatorService, { EmployeeDisplayInfo } from '../services/operatorService';

interface UseEmployeeDisplayInfoReturn {
  /** Get cached display name for an employee. Returns null if not yet fetched. */
  getDisplayName: (employeeNumber: string) => string | null;
  /** Get full cached employee info. Returns null if not yet fetched. */
  getEmployeeInfo: (employeeNumber: string) => EmployeeDisplayInfo | null;
  /** Fetch a single employee's display info. Returns the info and caches it. */
  fetchEmployee: (employeeNumber: string) => Promise<EmployeeDisplayInfo | null>;
  /** Fetch multiple employees' display info in one request. Caches all results. */
  fetchBatch: (employeeNumbers: string[]) => Promise<EmployeeDisplayInfo[]>;
  /** Whether a fetch is currently in progress. */
  isLoading: boolean;
}

export function useEmployeeDisplayInfo(): UseEmployeeDisplayInfoReturn {
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef<Map<string, EmployeeDisplayInfo>>(new Map());

  const getDisplayName = useCallback((employeeNumber: string): string | null => {
    const entry = cacheRef.current.get(employeeNumber);
    return entry ? entry.display_name : null;
  }, []);

  const getEmployeeInfo = useCallback((employeeNumber: string): EmployeeDisplayInfo | null => {
    return cacheRef.current.get(employeeNumber) || null;
  }, []);

  const fetchEmployee = useCallback(async (employeeNumber: string): Promise<EmployeeDisplayInfo | null> => {
    const cached = cacheRef.current.get(employeeNumber);
    if (cached) return cached;

    setIsLoading(true);
    try {
      const info = await operatorService.getEmployee(employeeNumber);
      if (info) {
        cacheRef.current.set(employeeNumber, info);
      }
      return info;
    } catch (error) {
      console.error('Failed to fetch employee display info for', employeeNumber, error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchBatch = useCallback(async (employeeNumbers: string[]): Promise<EmployeeDisplayInfo[]> => {
    const unique = [...new Set(employeeNumbers)];
    const misses = unique.filter(num => !cacheRef.current.has(num));

    if (misses.length === 0) {
      return unique
        .map(num => cacheRef.current.get(num))
        .filter((info): info is EmployeeDisplayInfo => info != null);
    }

    setIsLoading(true);
    try {
      const results = await operatorService.getEmployeeBatch(misses);
      for (const info of results) {
        cacheRef.current.set(info.employee_number, info);
      }
      return unique
        .map(num => cacheRef.current.get(num))
        .filter((info): info is EmployeeDisplayInfo => info != null);
    } catch (error) {
      console.error('Failed to batch fetch employee display info', error);
      return unique
        .map(num => cacheRef.current.get(num))
        .filter((info): info is EmployeeDisplayInfo => info != null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    getDisplayName,
    getEmployeeInfo,
    fetchEmployee,
    fetchBatch,
    isLoading,
  };
}

export default useEmployeeDisplayInfo;
